import {
  createModuleBlockRegistry,
  type ModuleBlockRegistration,
} from '@template-ui/blocks';
import {
  guidanceBlock,
  reviewRecordsBlock,
  informationBlock,
  requirementsEvidenceBlock,
  activityBlock,
  sharedNotesBlock,
  noteTimelineBlock,
  deadlinesBlock,
  deadlinesBlockV2,
} from './workflow-block-manifests';
export * from './workflow-block-manifests';
import {
  inmateSummaryBlock,
  inmateRvrsBlock,
  inmateProgrammingBlock,
  currentHoldingBlock,
  upcomingHearingsBlock,
  sdmfAnalysisBlock,
  sourceTwoDomainBlockManifests,
  historyBlockManifests,
  noticeBlockManifests,
  filteredHistoryBlockManifests,
  codedHistoryBlockManifests,
  headerHistoryBlockManifests,
} from '@app/phms/workflow-blocks/domain-block-manifests';
import { votingBlockManifests } from '@app/shared/workflow-voting/voting-block-manifests';

import { schedulingBlockManifests } from '@app/phms/workflow-blocks/scheduling-block-manifests';
import { hearingCompletionManifests } from '@app/phms/workflow-blocks/hearing-completion-manifests';
import { documentsBlock } from './workflow-documents-block-manifests';
import { caseSnapshotBlock } from '@app/phms/workflow-blocks/case-snapshot-block-manifests';
/** Shared by the authoring library and the real staff host; lookup never performs reads. */
export function createWorkflowBlockRegistry() {
  const modules: ModuleBlockRegistration[] = [
    {
      code: 'phms',
      version: 1,
      blocks: [{
        manifest: caseSnapshotBlock,
        load: () => import('@app/phms/workflow-blocks/case-snapshot-block.component').then(
          (m) => m.CaseSnapshotBlockComponent,
        ),
      }],
    },
    {
      code: 'hearing',
      version: 1,
      blocks: hearingCompletionManifests.map((manifest) => ({
        manifest,
        load: () => manifest.code === 'hearing.grant-review'
          ? import('@app/phms/workflow-blocks/hearing-grant-review-block.component').then(m => m.HearingGrantReviewBlockComponent)
          :
          import('@app/phms/workflow-blocks/hearing-completion-block.component').then(
            (m) => m.HearingCompletionBlockComponent,
          ),
      })),
    },
    {
      code: 'scheduling',
      version: 1,
      blocks: schedulingBlockManifests.map((manifest) => ({
        manifest,
        load: () =>
          import('@app/phms/workflow-blocks/scheduling-workspace-block.component').then(
            (m) => m.SchedulingWorkspaceBlockComponent,
          ),
      })),
    },
    {
      code: 'voting',
      version: 1,
      blocks: votingBlockManifests.map((manifest) => ({
        manifest,
        load: () =>
          import('@app/shared/workflow-voting/voting-workspace-block.component').then(
            (m) => m.VotingWorkspaceBlockComponent,
          ),
      })),
    },
    {
      code: 'workflow',
      version: 1,
      blocks: [
        {
          manifest: documentsBlock,
          load: () => import('./workflow-documents-block.component').then(
            (m) => m.WorkflowDocumentsBlockComponent,
          ),
        },
        {
          manifest: guidanceBlock,
          load: () =>
            import('./workflow-guidance-block.component').then(
              (m) => m.WorkflowGuidanceBlockComponent,
            ),
        },
        {
          manifest: reviewRecordsBlock,
          load: () =>
            import('./workflow-review-records-block.component').then(
              (m) => m.WorkflowReviewRecordsBlockComponent,
            ),
        },
        {
          manifest: informationBlock,
          load: () =>
            import('./workflow-information-block.component').then(
              (m) => m.WorkflowInformationBlockComponent,
            ),
        },
        {
          manifest: requirementsEvidenceBlock,
          load: () =>
            import('./workflow-requirements-evidence-block.component').then(
              (m) => m.WorkflowRequirementsEvidenceBlockComponent,
            ),
        },
        {
          manifest: activityBlock,
          load: () =>
            import('./workflow-activity-block.component').then(
              (m) => m.WorkflowActivityBlockComponent,
            ),
        },
        {
          manifest: sharedNotesBlock,
          load: () =>
            import('./workflow-shared-notes-block.component').then(
              (m) => m.WorkflowSharedNotesBlockComponent,
            ),
        },
        {
          manifest: noteTimelineBlock,
          load: () =>
            import('./workflow-note-timeline-block.component').then(
              (m) => m.WorkflowNoteTimelineBlockComponent,
            ),
        },
        {
          manifest: deadlinesBlock,
          load: () =>
            import('./workflow-deadlines-block.component').then(
              (m) => m.WorkflowDeadlinesBlockComponent,
            ),
        },
      ],
    },
    {
      code: 'platform.workflow-deadlines.v2',
      version: 2,
      blocks: [
        {
          manifest: deadlinesBlockV2,
          load: () =>
            import('./workflow-deadlines-block-v2.component').then(
              (m) => m.WorkflowDeadlinesBlockV2Component,
            ),
        },
      ],
    },
    {
      code: 'inmate',
      version: 1,
      blocks: [
        {
          manifest: inmateSummaryBlock,
          load: () =>
            import('@app/phms/workflow-blocks/inmate-summary-block.component').then(
              (m) => m.InmateSummaryBlockComponent,
            ),
        },
        {
          manifest: inmateRvrsBlock,
          load: () =>
            import('@app/phms/workflow-blocks/inmate-history-block.component').then(
              (m) => m.InmateRvrsBlockComponent,
            ),
        },
        {
          manifest: inmateProgrammingBlock,
          load: () =>
            import('@app/phms/workflow-blocks/inmate-history-block.component').then(
              (m) => m.InmateProgrammingBlockComponent,
            ),
        },
      ],
    },
    {
      code: 'institution',
      version: 1,
      blocks: [
        {
          manifest: currentHoldingBlock,
          load: () =>
            import('@app/phms/workflow-blocks/current-holding-block.component').then(
              (m) => m.CurrentHoldingBlockComponent,
            ),
        },
      ],
    },
    {
      code: 'hearings',
      version: 1,
      blocks: [
        {
          manifest: upcomingHearingsBlock,
          load: () =>
            import('@app/phms/workflow-blocks/upcoming-hearings-block.component').then(
              (m) => m.UpcomingHearingsBlockComponent,
            ),
        },
      ],
    },
    {
      code: 'sdmf',
      version: 1,
      blocks: [
        {
          manifest: sdmfAnalysisBlock,
          load: () =>
            import('@app/phms/workflow-blocks/sdmf-analysis-block.component').then(
              (m) => m.SdmfAnalysisBlockComponent,
            ),
        },
      ],
    },
    {
      code: 'notices',
      version: 1,
      blocks: noticeBlockManifests.map((manifest) => ({
        manifest,
        load: () =>
          import('@app/phms/workflow-blocks/notice-workspace-block.component').then(
            (m) => m.NoticeWorkspaceBlockComponent,
          ),
      })),
    },
    ...['inmate', 'institution', 'hearings', 'sdmf'].map((provider) => ({
      code: provider,
      version: provider === 'inmate' ? 5 : 2,
      blocks: [
        ...sourceTwoDomainBlockManifests,
        ...historyBlockManifests,
        ...filteredHistoryBlockManifests,
        ...codedHistoryBlockManifests,
        ...headerHistoryBlockManifests,
      ]
        .filter((manifest) => manifest.provider === provider)
        .map((manifest) => ({
          manifest,
          load: () => {
            switch (manifest.code) {
              case 'inmate.summary':
                return import('@app/phms/workflow-blocks/inmate-summary-block.component').then(
                  (m) => m.InmateSummaryBlockComponent,
                );
              case 'inmate.rvrs':
                return import('@app/phms/workflow-blocks/inmate-history-block.component').then(
                  (m) => m.InmateRvrsBlockComponent,
                );
              case 'inmate.programming':
                return import('@app/phms/workflow-blocks/inmate-history-block.component').then(
                  (m) => m.InmateProgrammingBlockComponent,
                );
              case 'institution.current-holding':
                return import('@app/phms/workflow-blocks/current-holding-block.component').then(
                  (m) => m.CurrentHoldingBlockComponent,
                );
              case 'hearings.upcoming':
                return import('@app/phms/workflow-blocks/upcoming-hearings-block.component').then(
                  (m) => m.UpcomingHearingsBlockComponent,
                );
              case 'sdmf.analysis':
                return import('@app/phms/workflow-blocks/sdmf-analysis-block.component').then(
                  (m) => m.SdmfAnalysisBlockComponent,
                );
              case 'inmate.offenses':
                return import('@app/phms/workflow-blocks/recorded-history-block.component').then(
                  (m) => m.InmateOffensesBlockComponent,
                );
              case 'inmate.incarceration-history':
                return import('@app/phms/workflow-blocks/recorded-history-block.component').then(
                  (m) => m.IncarcerationHistoryBlockComponent,
                );
              case 'hearings.history':
                return import('@app/phms/workflow-blocks/recorded-history-block.component').then(
                  (m) => m.HearingHistoryBlockComponent,
                );
              default:
                throw new Error('No exact renderer installed.');
            }
          },
        })),
    })),
  ];
  const combined = new Map<string, ModuleBlockRegistration>();
  for (const module of modules) {
    const existing = combined.get(module.code);
    combined.set(
      module.code,
      existing
        ? {
            ...existing,
            version: Math.max(existing.version, module.version),
            blocks: [...existing.blocks, ...module.blocks],
          }
        : module,
    );
  }
  return createModuleBlockRegistry([...combined.values()]);
}
